// PiecesPage.jsx
import React from 'react';
import Seo, { breadcrumbLd } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import { useLang } from '../i18n';

const PIECES = {
  tr: [
    { k: '王 / 玉', p: '—', name: 'Şah (Ōshō / Gyokushō)', move: 'Her yöne bir kare.', promo: 'Terfi etmez. Mat edilirse oyun biter.' },
    { k: '飛', p: '龍', name: 'Kale (Hisha)', move: 'Yatay ve dikey, istediği kadar kare.', promo: 'Ejderha (Ryū): kale hareketine ek olarak çaprazlara bir kare.' },
    { k: '角', p: '馬', name: 'Fil (Kakugyō)', move: 'Çapraz, istediği kadar kare.', promo: 'At (Uma): fil hareketine ek olarak düz yönlere bir kare.' },
    { k: '金', p: '—', name: 'Altın General (Kin)', move: 'Öne, öne çaprazlara, yanlara ve geriye bir kare (6 yön).', promo: 'Terfi etmez.' },
    { k: '銀', p: '全', name: 'Gümüş General (Gin)', move: 'Öne ve dört çapraza bir kare (5 yön).', promo: 'Altın general gibi hareket eder.' },
    { k: '桂', p: '圭', name: 'At (Keima)', move: 'İki ileri bir yana zıplar; araya giren taşları atlar. Sadece ileri.', promo: 'Altın general gibi hareket eder.' },
    { k: '香', p: '杏', name: 'Mızrak (Kyōsha)', move: 'Sadece düz ileri, istediği kadar kare.', promo: 'Altın general gibi hareket eder.' },
    { k: '歩', p: 'と', name: 'Piyade (Fuhyō)', move: 'Bir kare düz ileri; aldığı yön de aynı.', promo: 'Tokin: altın general gibi hareket eder.' },
  ],
  en: [
    { k: '王 / 玉', p: '—', name: 'King (Ōshō / Gyokushō)', move: 'One square in any direction.', promo: 'Does not promote. Checkmate ends the game.' },
    { k: '飛', p: '龍', name: 'Rook (Hisha)', move: 'Any number of squares orthogonally.', promo: 'Dragon (Ryū): rook moves plus one square diagonally.' },
    { k: '角', p: '馬', name: 'Bishop (Kakugyō)', move: 'Any number of squares diagonally.', promo: 'Horse (Uma): bishop moves plus one square orthogonally.' },
    { k: '金', p: '—', name: 'Gold General (Kin)', move: 'One square forward, diagonally forward, sideways or straight back (6 directions).', promo: 'Does not promote.' },
    { k: '銀', p: '全', name: 'Silver General (Gin)', move: 'One square forward or diagonally in any direction (5 directions).', promo: 'Moves like a gold general.' },
    { k: '桂', p: '圭', name: 'Knight (Keima)', move: 'Jumps two forward and one sideways, over other pieces. Forward only.', promo: 'Moves like a gold general.' },
    { k: '香', p: '杏', name: 'Lance (Kyōsha)', move: 'Any number of squares straight forward.', promo: 'Moves like a gold general.' },
    { k: '歩', p: 'と', name: 'Pawn (Fuhyō)', move: 'One square straight forward; captures the same way.', promo: 'Tokin: moves like a gold general.' },
  ],
};

const C = {
  tr: {
    title: 'Şogi Taşları ve Hareketleri',
    seoDesc: "Şogide 8 taş çeşidi: şah, kale, fil, altın ve gümüş general, at, mızrak ve piyade. Her taşın hareketi, Japonca adı ve terfi edince nasıl değiştiği.",
    lead: "Şogide 8 taş çeşidi vardır. Şah ve altın general dışındaki 6 taş, rakibin son üç sırasında terfi edip güçlenebilir.",
    th: ['Taş', 'Terfi', 'Adı', 'Hareket', 'Terfi edince'],
    note: 'Terfi isteğe bağlıdır; ancak piyade ve mızrak son sıraya, at son iki sıraya giderse terfi zorunludur, çünkü aksi halde bir daha hareket edemezler.',
  },
  en: {
    title: 'Shogi Pieces and How They Move',
    seoDesc: 'The 8 shogi pieces: king, rook, bishop, gold and silver generals, knight, lance and pawn. How each moves, its Japanese name and what it becomes when promoted.',
    lead: 'Shogi has 8 kinds of pieces. All except the king and the gold general can promote in the last three ranks.',
    th: ['Piece', 'Promoted', 'Name', 'Movement', 'When promoted'],
    note: 'Promotion is optional, except that a pawn or lance reaching the last rank, or a knight reaching the last two ranks, must promote — otherwise it could never move again.',
  },
};

export default function PiecesPage() {
  const { lang, to, t } = useLang();
  const c = C[lang];
  const rows = PIECES[lang];

  return (
    <>
      <Seo
        title={c.title}
        description={c.seoDesc}
        jsonLd={breadcrumbLd([{ name: t('brand'), path: to('home') }, { name: c.title, path: to('pieces') }])}
      />
      <PageHeader title={c.title} lead={c.lead} crumbs={[{ label: t('nav.pieces') }]} />
      <div className="container">
        <div className="row g-3">
          {rows.map((p) => (
            <div className="col-12 col-md-6" key={p.name}>
              <article className="panel h-100 d-flex gap-3">
                <div className="text-center flex-shrink-0" style={{ width: 76 }}>
                  <div className="hb-koma jp mx-auto" style={{ width: 54, fontSize: '1.5rem' }}>{p.k.split(' / ')[0]}</div>
                  {p.p !== '—' && (
                    <div className="hb-koma jp mx-auto mt-2" style={{ width: 44, fontSize: '1.1rem', color: '#b5121b' }}>{p.p}</div>
                  )}
                </div>
                <div>
                  <h2 className="h4 mb-1">{p.name} <span className="jp text-muted fs-6">{p.k}</span></h2>
                  <p className="mb-1"><strong>{c.th[3]}:</strong> {p.move}</p>
                  <p className="mb-0 text-muted"><strong>{c.th[4]}:</strong> {p.promo}</p>
                </div>
              </article>
            </div>
          ))}
        </div>
        <p className="small text-muted mt-3">{c.note}</p>
      </div>
    </>
  );
}
